type FlowSeriesPoint = {
  label: string;
  yesBuyFlow: bigint;
  noBuyFlow: bigint;
  yesSellFlow: bigint;
  noSellFlow: bigint;
};

type WindowFlowPoint = {
  label: string;
  yesBuyFlow: bigint;
  yesSellFlow: bigint;
  noBuyFlow: bigint;
  noSellFlow: bigint;
};

type RendererConfig = {
  yesOutcomeLabel: string;
  noOutcomeLabel: string;
  formatValue: (value: bigint) => string;
  formatSigned: (value: bigint) => string;
  yesIsTrending: boolean;
};

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function renderFlowSplitSvg(series: FlowSeriesPoint[], formatToken: (value: bigint) => string): string {
  const width = 760;
  const height = 180;
  const padX = 14;
  const padY = 14;
  const innerWidth = width - padX * 2;
  const innerHeight = height - padY * 2;
  const denom = Math.max(series.length - 1, 1);
  const centerValue = 50;
  const movementSpan = 40n;
  const movementCurve = 0.66;
  const signed = (value: bigint): bigint => {
    const abs = value < 0n ? -value : value;
    return value < 0n ? -abs : abs;
  };
  let yesCumulativeNet = 0n;
  let noCumulativeNet = 0n;

  const points = series.map((point, index) => {
    const x = padX + (innerWidth * index) / denom;
    yesCumulativeNet += point.yesBuyFlow - point.yesSellFlow;
    noCumulativeNet += point.noBuyFlow - point.noSellFlow;
    return {
      x,
      yesCumulativeNet: signed(yesCumulativeNet),
      noCumulativeNet: signed(noCumulativeNet),
    };
  });

  const maxAbsNet = points.reduce((acc, point) => {
    const yesAbs = point.yesCumulativeNet < 0n ? -point.yesCumulativeNet : point.yesCumulativeNet;
    const noAbs = point.noCumulativeNet < 0n ? -point.noCumulativeNet : point.noCumulativeNet;
    return yesAbs > (noAbs > acc ? noAbs : acc) ? yesAbs : (noAbs > acc ? noAbs : acc);
  }, 0n);

  const scaleOffset = (value: bigint): bigint => {
    if (maxAbsNet <= 0n || value === 0n) return 0n;
    const abs = value < 0n ? -value : value;
    const normalized = Number(abs) / Number(maxAbsNet);
    return BigInt(Math.round(Number(movementSpan) * Math.pow(normalized, movementCurve)));
  };

  const seed = 100n;

  const projectY = (value: bigint): number => {
    const ratio = Number(value) / Number(seed);
    return padY + innerHeight - ratio * innerHeight;
  };

  const buyPoints = points.map((point) => {
    const yesOffset = scaleOffset(point.yesCumulativeNet);
    const signedOffset = point.yesCumulativeNet >= 0n ? yesOffset : -yesOffset;
    return { x: point.x, y: projectY(BigInt(centerValue) + signedOffset) };
  });
  const sellPoints = points.map((point) => {
    const noOffset = scaleOffset(point.noCumulativeNet);
    const signedOffset = point.noCumulativeNet >= 0n ? -noOffset : noOffset;
    return { x: point.x, y: projectY(BigInt(centerValue) + signedOffset) };
  });

  const buildStepPath = (pathPoints: Array<{ x: number; y: number }>): string => {
    if (pathPoints.length === 0) return '';
    let path = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
    for (let index = 1; index < pathPoints.length; index += 1) {
      const current = pathPoints[index];
      path += ` H ${current.x} V ${current.y}`;
    }
    return path;
  };

  const buildAreaPath = (pathPoints: Array<{ x: number; y: number }>): string => {
    if (pathPoints.length === 0) return '';
    const last = pathPoints[pathPoints.length - 1];
    const first = pathPoints[0];
    const centerY = projectY(BigInt(centerValue));
    let path = `M ${first.x} ${centerY}`;
    path += ` L ${first.x} ${first.y}`;
    for (let index = 1; index < pathPoints.length; index += 1) {
      const current = pathPoints[index];
      path += ` H ${current.x} V ${current.y}`;
    }
    path += ` H ${last.x} L ${last.x} ${centerY} Z`;
    return path;
  };

  const buyLine = buildStepPath(buyPoints);
  const sellLine = buildStepPath(sellPoints);

  const gridTicks = [0.25, 0.5, 0.75].map((ratio) => Number(seed) * ratio);
  const grid = gridTicks.map((tick) => {
    const y = projectY(BigInt(Math.round(tick)));
    return `<line x1="${padX}" y1="${y}" x2="${width - padX}" y2="${y}" stroke="rgba(126,165,192,.18)" stroke-width="1" />`;
  }).join('');

  const labels = series.map((point) => point.label);
  const labelText = labels.length > 0
    ? `<div class="mono analytics-chart-labels"><span>${labels[0]}</span><span>${labels[Math.floor(labels.length / 2)] || labels[0]}</span><span>${labels[labels.length - 1]}</span></div>`
    : '';

  return `
    <div style="overflow-x:auto;">
      <svg viewBox="0 0 ${width} ${height}" width="100%" height="180" role="img" aria-label="Flow split chart">
        <defs>
          <linearGradient id="analyticsFlowBand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#2ec989" stop-opacity="0.24" />
            <stop offset="100%" stop-color="#2ec989" stop-opacity="0.03" />
          </linearGradient>
          <linearGradient id="analyticsSellBand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#e8637a" stop-opacity="0.20" />
            <stop offset="100%" stop-color="#e8637a" stop-opacity="0.03" />
          </linearGradient>
        </defs>
        ${grid}
        <path d="${buildAreaPath(buyPoints)}" fill="url(#analyticsFlowBand)" />
        <path d="${buildAreaPath(sellPoints)}" fill="url(#analyticsSellBand)" />
        <line x1="${padX}" y1="${projectY(50n)}" x2="${width - padX}" y2="${projectY(50n)}" stroke="rgba(226,243,255,.32)" stroke-width="1.2" />
        <path d="${buyLine}" fill="none" stroke="#2ec989" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
        <path d="${sellLine}" fill="none" stroke="#e8637a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
        <line id="analyticsFlowNeutralMask" x1="${padX}" y1="${projectY(50n)}" x2="${width - padX}" y2="${projectY(50n)}" stroke="rgba(122,136,152,.92)" stroke-width="2.8" stroke-linecap="round" />
      </svg>
    </div>
    ${labelText}
  `;
}

export function renderBarHistorySvg(series: WindowFlowPoint[], config: RendererConfig): string {
  const points = series.map((point) => ({
    ...point,
    yesNet: point.yesBuyFlow - point.yesSellFlow,
    noNet: point.noBuyFlow - point.noSellFlow,
  }));

  const maxAbs = points.reduce((acc, point) => {
    const yesAbs = point.yesNet < 0n ? -point.yesNet : point.yesNet;
    const noAbs = point.noNet < 0n ? -point.noNet : point.noNet;
    const local = yesAbs > noAbs ? yesAbs : noAbs;
    return local > acc ? local : acc;
  }, 1n);

  const scaleWidth = (value: bigint): number => {
    const abs = value < 0n ? -value : value;
    return Math.max(8, Math.round((Number(abs) / Number(maxAbs)) * 100));
  };

  const yesToneClass = config.yesIsTrending ? 'is-bid' : 'is-ask';
  const noToneClass = config.yesIsTrending ? 'is-ask' : 'is-bid';
  const yesOutcomeClass = config.yesIsTrending ? 'analytics-outcome-leading' : 'analytics-outcome-trailing';
  const noOutcomeClass = config.yesIsTrending ? 'analytics-outcome-trailing' : 'analytics-outcome-leading';

  const rows = points.map((point) => {
    const yesLabel = config.formatSigned(point.yesNet);
    const noLabel = config.formatSigned(point.noNet);
    const compactSummary = `${escapeAttr(point.label)} · ${escapeAttr(config.yesOutcomeLabel)} ${escapeAttr(yesLabel)} / ${escapeAttr(config.noOutcomeLabel)} ${escapeAttr(noLabel)}`;
    return `
      <div class="analytics-book-row" role="row">
        <div class="mono analytics-book-time" role="cell">${point.label}</div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.yesOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.yesBuyFlow))} | Sell ${escapeAttr(config.formatValue(point.yesSellFlow))} | Net ${escapeAttr(yesLabel)}">
          <div class="analytics-book-bar ${yesToneClass}" style="width:${scaleWidth(point.yesNet)}%"></div>
          <div class="mono analytics-book-value ${yesToneClass}">${yesLabel}</div>
        </div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.noOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.noBuyFlow))} | Sell ${escapeAttr(config.formatValue(point.noSellFlow))} | Net ${escapeAttr(noLabel)}">
          <div class="analytics-book-bar ${noToneClass}" style="width:${scaleWidth(point.noNet)}%"></div>
          <div class="mono analytics-book-value ${noToneClass}">${noLabel}</div>
        </div>
      </div>
      <details class="analytics-compact-row">
        <summary class="mono analytics-compact-summary">${compactSummary}</summary>
        <div class="analytics-compact-body">
          <div class="analytics-compact-line">
            <span class="mono analytics-compact-k ${yesOutcomeClass}">${config.yesOutcomeLabel} Net</span>
            <span class="mono analytics-compact-v ${yesToneClass}">${yesLabel}</span>
          </div>
          <div class="analytics-book-cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.yesOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.yesBuyFlow))} | Sell ${escapeAttr(config.formatValue(point.yesSellFlow))}">
            <div class="analytics-book-bar ${yesToneClass}" style="width:${scaleWidth(point.yesNet)}%"></div>
            <div class="mono analytics-book-value ${yesToneClass}">${yesLabel}</div>
          </div>
          <div class="analytics-compact-line">
            <span class="mono analytics-compact-k ${noOutcomeClass}">${config.noOutcomeLabel} Net</span>
            <span class="mono analytics-compact-v ${noToneClass}">${noLabel}</span>
          </div>
          <div class="analytics-book-cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.noOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.noBuyFlow))} | Sell ${escapeAttr(config.formatValue(point.noSellFlow))}">
            <div class="analytics-book-bar ${noToneClass}" style="width:${scaleWidth(point.noNet)}%"></div>
            <div class="mono analytics-book-value ${noToneClass}">${noLabel}</div>
          </div>
        </div>
      </details>
    `;
  }).join('');

  return `
    <div class="analytics-book" role="table" aria-label="Net flow chart">
      <div class="analytics-book-header" role="row">
        <div class="mono analytics-book-head">Time</div>
        <div class="mono analytics-book-head ${yesOutcomeClass}">${config.yesOutcomeLabel} Net</div>
        <div class="mono analytics-book-head ${noOutcomeClass}">${config.noOutcomeLabel} Net</div>
      </div>
      ${rows}
    </div>
  `;
}

export function renderStackedFlowSvg(series: WindowFlowPoint[], config: RendererConfig): string {
  const points = series.map((point) => ({
    ...point,
    maxCell: [point.yesBuyFlow, point.yesSellFlow, point.noBuyFlow, point.noSellFlow].reduce((acc, value) => value > acc ? value : acc, 0n),
  }));

  const maxCell = points.reduce((acc, point) => point.maxCell > acc ? point.maxCell : acc, 1n);
  const widthPct = (value: bigint): number => Math.max(8, Math.round((Number(value) / Number(maxCell)) * 100));
  const yesOutcomeClass = config.yesIsTrending ? 'analytics-outcome-leading' : 'analytics-outcome-trailing';
  const noOutcomeClass = config.yesIsTrending ? 'analytics-outcome-trailing' : 'analytics-outcome-leading';

  const rows = points.map((point) => {
    const total = point.yesBuyFlow + point.yesSellFlow + point.noBuyFlow + point.noSellFlow;
    return `
      <div class="analytics-book-row analytics-book-row-4" role="row">
        <div class="mono analytics-book-time" role="cell">${point.label}</div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.yesOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.yesBuyFlow))}">
          <div class="analytics-book-bar is-bid" style="width:${widthPct(point.yesBuyFlow)}%"></div>
          <div class="mono analytics-book-value is-bid">${config.formatValue(point.yesBuyFlow)}</div>
        </div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.yesOutcomeLabel)} Sell ${escapeAttr(config.formatValue(point.yesSellFlow))}">
          <div class="analytics-book-bar is-ask" style="width:${widthPct(point.yesSellFlow)}%"></div>
          <div class="mono analytics-book-value is-ask">${config.formatValue(point.yesSellFlow)}</div>
        </div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.noOutcomeLabel)} Buy ${escapeAttr(config.formatValue(point.noBuyFlow))}">
          <div class="analytics-book-bar is-bid-alt" style="width:${widthPct(point.noBuyFlow)}%"></div>
          <div class="mono analytics-book-value is-bid-alt">${config.formatValue(point.noBuyFlow)}</div>
        </div>
        <div class="analytics-book-cell" role="cell" title="${escapeAttr(point.label)} | ${escapeAttr(config.noOutcomeLabel)} Sell ${escapeAttr(config.formatValue(point.noSellFlow))}">
          <div class="analytics-book-bar is-ask-alt" style="width:${widthPct(point.noSellFlow)}%"></div>
          <div class="mono analytics-book-value is-ask-alt">${config.formatValue(point.noSellFlow)}</div>
        </div>
      </div>
      <details class="analytics-compact-row">
        <summary class="mono analytics-compact-summary">${escapeAttr(point.label)} · Total ${escapeAttr(config.formatValue(total))}</summary>
        <div class="analytics-compact-body analytics-compact-grid-2">
          <div class="analytics-compact-line"><span class="mono analytics-compact-k ${yesOutcomeClass}">${config.yesOutcomeLabel} Buy</span><span class="mono analytics-compact-v is-bid">${config.formatValue(point.yesBuyFlow)}</span></div>
          <div class="analytics-compact-line"><span class="mono analytics-compact-k ${yesOutcomeClass}">${config.yesOutcomeLabel} Sell</span><span class="mono analytics-compact-v is-ask">${config.formatValue(point.yesSellFlow)}</span></div>
          <div class="analytics-compact-line"><span class="mono analytics-compact-k ${noOutcomeClass}">${config.noOutcomeLabel} Buy</span><span class="mono analytics-compact-v is-bid-alt">${config.formatValue(point.noBuyFlow)}</span></div>
          <div class="analytics-compact-line"><span class="mono analytics-compact-k ${noOutcomeClass}">${config.noOutcomeLabel} Sell</span><span class="mono analytics-compact-v is-ask-alt">${config.formatValue(point.noSellFlow)}</span></div>
        </div>
      </details>
    `;
  }).join('');

  return `
    <div class="analytics-book" role="table" aria-label="Buy vs sell flow chart">
      <div class="analytics-book-header analytics-book-header-5" role="row">
        <div class="mono analytics-book-head">Time</div>
        <div class="mono analytics-book-head ${yesOutcomeClass}">${config.yesOutcomeLabel} Buy</div>
        <div class="mono analytics-book-head ${yesOutcomeClass}">${config.yesOutcomeLabel} Sell</div>
        <div class="mono analytics-book-head ${noOutcomeClass}">${config.noOutcomeLabel} Buy</div>
        <div class="mono analytics-book-head ${noOutcomeClass}">${config.noOutcomeLabel} Sell</div>
      </div>
      ${rows}
    </div>
  `;
}
